/**
 * Revenue Chart Component
 * Displays daily revenue as a bar chart
 */

import React from 'react';
import { RevenueData } from '../../types';

interface RevenueChartProps {
  data: RevenueData[];
}

export const RevenueChart: React.FC<RevenueChartProps> = ({ data }) => {
  // Enhanced logging for debugging
  console.log('📊 RevenueChart Component - Data received:', {
    dataLength: data?.length,
    hasData: !!data,
    totalRevenue: (data as any)?.totalRevenue,
    totalOrderCount: (data as any)?.totalOrderCount,
    sampleData: data ? data.slice(0, 3) : null
  });

  if (!data || data.length === 0) {
    console.warn('⚠️ RevenueChart: No data provided');
    return (
      <div className="flex items-center justify-center h-48 text-gray-400">
        <div className="text-center">
          <p className="font-medium">No data available</p>
          <p className="text-sm">No orders found for selected period</p>
        </div>
      </div>
    );
  }

  // Use metadata from aggregated data array, fallback to calculation if not present
  const totalRevenue = (data as any).totalRevenue || data.reduce((sum, d) => sum + d.revenue, 0);
  const totalOrderCount = (data as any).totalOrderCount || data.reduce((sum, d) => sum + (d.orderCount || 0), 0);

  console.log('📊 RevenueChart - Processed metrics:', {
    totalRevenue,
    totalOrderCount,
    dataPoints: data.length,
    nonZeroPoints: data.filter((d: any) => d.revenue > 0).length
  });

  // Show chart even if revenue is 0 - orders might be pending/draft status
  if (totalOrderCount === 0) {
    console.warn('⚠️ RevenueChart: No orders found');
    return (
      <div className="flex items-center justify-center h-48 text-gray-400">
        <div className="text-center">
          <p className="font-medium">No orders placed yet</p>
          <p className="text-sm">Orders will appear here once customers place them</p>
        </div>
      </div>
    );
  }

  // Log when showing chart with 0 revenue
  if (totalRevenue === 0 && totalOrderCount > 0) {
    console.log('💡 RevenueChart: Showing chart with 0 revenue - orders exist but may be in pending/draft status');
  }

  const maxRevenue = Math.max(...data.map(d => d.revenue), 1);

  // Determine bar sizing and label interval based on data length
  // Short ranges (<=14 days) get generous spacing; longer ranges become scrollable with thinner bars
  const isCompact = data.length <= 14;
  const barWidth = isCompact ? 28 : data.length <= 31 ? 16 : 10;
  const columnGap = isCompact ? 12 : data.length <= 31 ? 6 : 2;

  let labelInterval = 1;
  if (data.length > 60) {
    labelInterval = 7;
  } else if (data.length > 31) {
    labelInterval = 3;
  } else if (data.length > 14) {
    labelInterval = 2;
  }

  const chartHeightPx = 160;
  const maxBarPx = chartHeightPx - 24; // leave room for value label above

  return (
    <div className="space-y-4">
      {/* Legend */}
      <div className="flex items-center gap-3 px-1">
        <div className="flex items-center gap-2">
          <div className="w-4 h-3 bg-gradient-to-t from-green-600 to-green-400 rounded-sm shadow-sm"></div>
          <span className="text-xs font-medium text-gray-700">Daily Revenue</span>
        </div>
      </div>

      {/* Scrollable container for longer date ranges */}
      <div className="overflow-x-auto">
        <div
          className="bg-gradient-to-br from-gray-50 to-gray-100 p-4 rounded-lg border border-gray-200"
          style={{ minWidth: isCompact ? '100%' : `${data.length * (barWidth + columnGap) + 32}px` }}
        >
          <div
            className={`flex items-end ${isCompact ? 'justify-around' : 'justify-start'}`}
            style={{ height: `${chartHeightPx}px`, gap: `${columnGap}px` }}
          >
            {data.map((item, idx) => {
              const barPx = item.revenue > 0
                ? Math.max((item.revenue / maxRevenue) * maxBarPx, 4)
                : 0;
              const displayRevenue = item.revenue > 0 ? `₹${(item.revenue / 1000).toFixed(1)}K` : '';
              const showValue = isCompact && item.revenue > 0;

              return (
                <div
                  key={idx}
                  className="flex-shrink-0 flex flex-col items-center justify-end h-full"
                  style={{ width: `${barWidth}px` }}
                  title={`${item.date}: ${item.revenue > 0 ? displayRevenue : '₹0'} • ${item.orderCount} order${item.orderCount !== 1 ? 's' : ''}`}
                >
                  {showValue && (
                    <div className="text-[10px] font-semibold text-gray-700 mb-1 whitespace-nowrap leading-tight">
                      {displayRevenue}
                    </div>
                  )}
                  {item.revenue > 0 ? (
                    <div
                      className="w-full bg-gradient-to-t from-green-600 to-green-400 rounded-t-md transition-all duration-300 hover:from-green-700 hover:to-green-500 shadow-sm"
                      style={{ height: `${barPx}px` }}
                    />
                  ) : (
                    <div className="w-full border-b border-dashed border-gray-300" style={{ height: '1px' }} />
                  )}
                </div>
              );
            })}
          </div>

          {/* Date labels row — aligned under each bar column */}
          <div
            className={`flex ${isCompact ? 'justify-around' : 'justify-start'} mt-2`}
            style={{ gap: `${columnGap}px` }}
          >
            {data.map((item, idx) => {
              const shouldShowLabel = idx % labelInterval === 0 || idx === data.length - 1;
              return (
                <div
                  key={`label-${idx}`}
                  className="flex-shrink-0 text-center"
                  style={{ width: `${barWidth}px` }}
                >
                  {shouldShowLabel && (
                    <span className="text-[10px] text-gray-600 font-medium whitespace-nowrap">
                      {new Date(item.date + 'T00:00:00').toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short'
                      })}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Chart metrics */}
      <div className="grid grid-cols-3 gap-3">
        {/* Total Revenue */}
        <div className="p-3 bg-gradient-to-br from-green-50 to-gray-50 rounded-lg border border-green-100">
          <div className="text-xs text-gray-600 font-medium">Total Revenue</div>
          <div className="text-lg font-bold text-green-600 mt-1">
            ₹{(totalRevenue / 1000).toFixed(1)}K
          </div>
          <div className="text-xs text-gray-500 mt-1">{data.length} days</div>
        </div>

        {/* Peak Revenue */}
        <div className="p-3 bg-gradient-to-br from-blue-50 to-gray-50 rounded-lg border border-blue-100">
          <div className="text-xs text-gray-600 font-medium">Peak Daily</div>
          <div className="text-lg font-bold text-blue-600 mt-1">
            ₹{(maxRevenue / 1000).toFixed(1)}K
          </div>
          <div className="text-xs text-gray-500 mt-1">Highest day</div>
        </div>

        {/* Order Count */}
        <div className="p-3 bg-gradient-to-br from-purple-50 to-gray-50 rounded-lg border border-purple-100">
          <div className="text-xs text-gray-600 font-medium">Total Orders</div>
          <div className="text-lg font-bold text-purple-600 mt-1">
            {totalOrderCount}
          </div>
          <div className="text-xs text-gray-500 mt-1">All statuses</div>
        </div>
      </div>
    </div>
  );
};

export default RevenueChart;
